/**
 * Contractor Profile page (CP-3.2).
 *
 * Multi-step profile manager covering:
 *  - Basics (company name, website, bios, years, public contact)
 *  - Crew capability + lead/helper acceptance
 *  - Hourly rate + currency
 *  - Service regions (multi-select from catalog)
 *  - Skills (multi-select with proficiency)
 *  - Certifications (catalog-driven; CRUD)
 *  - Languages (multi-select with proficiency)
 *  - Travel preferences
 *  - Insurance details
 *  - Media: profile photo + company logo (S3-compatible upload)
 *  - Per-field visibility configuration view
 *  - Live profile completeness meter (admin-configurable weights)
 */
import React, { useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { WorkforceOfferingEditor } from "@/components/WorkforceOfferingEditor";
import { api, apiUpload } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const SECTIONS = [
  { id: "basics", label: "Basics" },
  { id: "media", label: "Photo & Logo" },
  { id: "crew", label: "Crew Capability" },
  { id: "rate", label: "Workforce Offering" },
  { id: "regions", label: "Service Regions" },
  { id: "skills", label: "Skills" },
  { id: "certs", label: "Certifications" },
  { id: "langs", label: "Languages" },
  { id: "travel", label: "Travel" },
  { id: "insurance", label: "Insurance" },
  { id: "visibility", label: "Visibility" },
];

const VISIBILITY_BADGE = {
  private: { label: "Private", cls: "avcp-pill", note: "Owner & Super Admin only" },
  platform_only: { label: "Platform", cls: "avcp-pill", note: "Owner & platform admins only" },
  client_visible: { label: "Clients", cls: "avcp-pill ok", note: "Authenticated clients see it" },
  public: { label: "Public", cls: "avcp-pill ok", note: "Anyone (future public pages)" },
};

function VisibilityBadge({ mode, field }) {
  const v = VISIBILITY_BADGE[mode] || VISIBILITY_BADGE.private;
  const tid = field ? `cp-visibility-field-${field}` : TID.cpVisibilityField;
  return (
    <span className={v.cls} title={v.note} data-testid={tid}>{v.label}</span>
  );
}

function Section({ id, title, children, complete }) {
  return (
    <section className="avcp-card p-6 mb-6" data-testid={`cp-section-${id}`}>
      <header className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
        {complete !== undefined && (
          <span className={`avcp-pill ${complete ? "ok" : ""}`}>
            {complete ? "Complete" : "Incomplete"}
          </span>
        )}
      </header>
      {children}
    </section>
  );
}

function Field({ label, hint, vis, children }) {
  return (
    <label className="block mb-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium text-[var(--avcp-ink)]">{label}</span>
        {vis && <VisibilityBadge mode={vis} />}
      </div>
      {children}
      {hint && <p className="text-xs text-[var(--avcp-ink-soft)] mt-1">{hint}</p>}
    </label>
  );
}

function CompletenessMeter({ score, components }) {
  const items = Object.entries(components || {});
  const total = items.reduce((s, [, v]) => s + Number(v || 0), 0);
  return (
    <div className="avcp-card p-6 mb-6" data-testid={TID.cpCompleteness}>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold tracking-tight">Profile Completeness</h2>
        <span className="text-2xl font-bold tracking-tight">{score}<span className="text-base text-[var(--avcp-ink-soft)]">/100</span></span>
      </div>
      <div className="h-2 rounded-full bg-[var(--avcp-line)] overflow-hidden mb-4">
        <div className="h-full bg-[var(--avcp-primary)] transition-all" style={{ width: `${score}%` }} />
      </div>
      <ul className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
        {items.map(([k, v]) => (
          <li key={k} className="flex items-center justify-between px-2 py-1 rounded bg-[var(--avcp-bg-2)]">
            <span className="text-[var(--avcp-ink-soft)]">{k.replace(/_/g, " ")}</span>
            <span className="font-mono">+{v}</span>
          </li>
        ))}
      </ul>
      {total === 0 && (
        <p className="text-sm text-[var(--avcp-ink-soft)]">Complete the steps below to grow your score.</p>
      )}
    </div>
  );
}

export function ContractorProfile() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [profile, setProfile] = useState({});
  const [completeness, setCompleteness] = useState({ score: 0, components: {} });
  const [languages, setLanguages] = useState([]);
  const [skills, setSkills] = useState([]);
  const [regions, setRegions] = useState([]);
  const [certs, setCerts] = useState([]);
  const [catalogs, setCatalogs] = useState({ skills: [], regions: [], certifications: [], languages: [] });
  const [visibilityMap, setVisibilityMap] = useState({});
  const [photoUrl, setPhotoUrl] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [me, cSkills, cRegions, cCerts, cLangs, vis] = await Promise.all([
        api("/me/contractor-profile"),
        api("/catalog/skills"),
        api("/catalog/regions"),
        api("/catalog/certifications"),
        api("/catalog/languages"),
        api("/visibility/contractor-profile"),
      ]);
      setProfile(me.profile || {});
      setCompleteness(me.completeness || { score: 0, components: {} });
      setLanguages(me.languages || []);
      setSkills(me.skills || []);
      setRegions(me.regions || []);
      setCerts(me.certifications || []);
      setCatalogs({
        skills: cSkills.data || [],
        regions: cRegions.data || [],
        certifications: cCerts.data || [],
        languages: cLangs.data || [],
      });
      const map = {};
      (vis.data || []).forEach((row) => { map[row.field_name] = row.visibility; });
      setVisibilityMap(map);

      if (me.profile?.profile_photo_media_id) {
        try {
          const u = await api(`/me/contractor-profile/media/${me.profile.profile_photo_media_id}/urls`);
          setPhotoUrl(u.urls?.medium || u.urls?.original || u.urls?.thumbnail || null);
        } catch (_) { /* signed URL fetch may fail in dev with rotated keys */ }
      } else {
        setPhotoUrl(null);
      }
      if (me.profile?.company_logo_media_id) {
        try {
          const u = await api(`/me/contractor-profile/media/${me.profile.company_logo_media_id}/urls`);
          setLogoUrl(u.urls?.medium || u.urls?.original || u.urls?.thumbnail || null);
        } catch (_) { /* idem */ }
      } else {
        setLogoUrl(null);
      }
    } catch (e) {
      toast.error(e.message || "Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const cmpKeys = useMemo(() => Object.keys(completeness.components || {}), [completeness]);

  const isCmp = (k) => cmpKeys.includes(k);

  const setField = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  const saveBasics = async () => {
    setBusy(true);
    try {
      const r = await api("/me/contractor-profile", {
        method: "PUT",
        body: {
          company_name: profile.company_name || null,
          website: profile.website || null,
          short_bio: profile.short_bio || null,
          long_bio: profile.long_bio || null,
          years_experience: profile.years_experience ? Number(profile.years_experience) : null,
          public_email: profile.public_email || null,
          public_phone: profile.public_phone || null,
        },
      });
      setCompleteness(r.completeness);
      toast.success("Basics saved");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const saveCrew = async () => {
    setBusy(true);
    try {
      const r = await api("/me/contractor-profile", {
        method: "PUT",
        body: {
          crew_capability_kind: profile.crew_capability_kind || null,
          max_helpers: profile.max_helpers != null ? Number(profile.max_helpers) : null,
          accepts_lead_work: !!profile.accepts_lead_work,
          accepts_helper_work: !!profile.accepts_helper_work,
          visibility: profile.visibility || null,
        },
      });
      setCompleteness(r.completeness);
      toast.success("Crew capability saved");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const saveRate = async () => {
    setBusy(true);
    try {
      const r = await api("/me/contractor-profile", {
        method: "PUT",
        body: {
          hourly_rate: profile.hourly_rate ? Number(profile.hourly_rate) : null,
          currency: profile.currency || null,
        },
      });
      setCompleteness(r.completeness);
      toast.success("Rate saved");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const saveTravel = async () => {
    setBusy(true);
    try {
      const r = await api("/me/contractor-profile", {
        method: "PUT",
        body: {
          travel_radius_kind: profile.travel_radius_kind || null,
          willing_to_travel: !!profile.willing_to_travel,
          willing_to_fly: !!profile.willing_to_fly,
          willing_to_stay_overnight: !!profile.willing_to_stay_overnight,
        },
      });
      setCompleteness(r.completeness);
      toast.success("Travel preferences saved");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const saveInsurance = async () => {
    setBusy(true);
    try {
      const r = await api("/me/contractor-profile", {
        method: "PUT",
        body: {
          insurance_json: {
            provider: profile.insurance_provider || null,
            policy_number: profile.insurance_policy || null,
            coverage_amount: profile.insurance_coverage ? Number(profile.insurance_coverage) : null,
            currency: profile.insurance_currency || null,
          },
        },
      });
      setCompleteness(r.completeness);
      toast.success("Insurance saved");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const uploadMedia = async (file, path, setUrl, label) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Max 5 MB");
      return;
    }
    setBusy(true);
    try {
      const r = await apiUpload(path, file);
      setCompleteness(r.completeness);
      setUrl(r.urls?.medium || r.urls?.original || r.urls?.thumbnail || null);
      toast.success(`${label} uploaded`);
    } catch (e) {
      toast.error(e.message);
    } finally { setBusy(false); }
  };

  const toggleSkill = (code) => {
    setSkills((cur) => {
      if (cur.find((c) => c.code === code)) return cur.filter((c) => c.code !== code);
      return [...cur, { code, proficiency: "experienced" }];
    });
  };
  const setSkillProf = (code, p) => setSkills((cur) => cur.map((c) => (c.code === code ? { ...c, proficiency: p } : c)));
  const saveSkills = async () => {
    setBusy(true);
    try {
      const r = await api("/me/contractor-profile/skills", {
        method: "PUT",
        body: { skills: skills.map((s) => ({ code: s.code, proficiency: s.proficiency })) },
      });
      setCompleteness(r.completeness);
      toast.success("Skills saved");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const toggleRegion = (code) => {
    setRegions((cur) => {
      if (cur.find((c) => c.code === code)) return cur.filter((c) => c.code !== code);
      return [...cur, { code }];
    });
  };
  const saveRegions = async () => {
    setBusy(true);
    try {
      const r = await api("/me/contractor-profile/regions", {
        method: "PUT",
        body: { region_codes: regions.map((c) => c.code) },
      });
      setCompleteness(r.completeness);
      toast.success("Service regions saved");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const addLang = (code) => {
    if (!code || languages.find((l) => l.code === code)) return;
    setLanguages((cur) => [...cur, { code, proficiency: "fluent" }]);
  };
  const removeLang = (code) => setLanguages((cur) => cur.filter((l) => l.code !== code));
  const setLangProf = (code, p) => setLanguages((cur) => cur.map((l) => (l.code === code ? { ...l, proficiency: p } : l)));
  const saveLangs = async () => {
    setBusy(true);
    try {
      const r = await api("/me/contractor-profile/languages", {
        method: "PUT",
        body: { languages: languages.map((l) => ({ code: l.code, proficiency: l.proficiency })) },
      });
      setCompleteness(r.completeness);
      toast.success("Languages saved");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const addCert = async (code) => {
    if (!code) return;
    setBusy(true);
    try {
      const r = await api("/me/contractor-profile/certifications", { method: "POST", body: { code } });
      setCompleteness(r.completeness);
      const cert = catalogs.certifications.find((c) => c.code === code);
      setCerts((cur) => [...cur, { id: r.id, code, label: cert?.label || code, issuer: cert?.issuer }]);
      toast.success("Certification added");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const removeCert = async (id) => {
    setBusy(true);
    try {
      const r = await api(`/me/contractor-profile/certifications/${id}`, { method: "DELETE" });
      setCompleteness(r.completeness);
      setCerts((cur) => cur.filter((c) => c.id !== id));
      toast.success("Certification removed");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  if (loading) {
    return <Layout><div className="avcp-card p-6">Loading profile…</div></Layout>;
  }

  return (
    <Layout>
      <div data-testid={TID.cpRoot}>
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight">Contractor Profile</h1>
          <p className="text-[var(--avcp-ink-soft)] mt-1">
            Manage your independent contractor profile. Visibility per field is controlled
            by the platform configuration — current settings are shown beside each section.
          </p>
        </div>

        <CompletenessMeter score={completeness.score} components={completeness.components} />

        {/* Basics */}
        <Section id="basics" title="Basics" complete={isCmp("short_bio") && isCmp("long_bio")}>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Company name" vis={visibilityMap.company_name}>
              <input className="avcp-input" data-testid={TID.cpCompanyName}
                     value={profile.company_name || ""}
                     onChange={(e) => setField("company_name", e.target.value)} />
            </Field>
            <Field label="Website" vis={visibilityMap.website}>
              <input className="avcp-input" data-testid={TID.cpWebsite}
                     value={profile.website || ""}
                     onChange={(e) => setField("website", e.target.value)} />
            </Field>
            <Field label="Years of experience" vis={visibilityMap.years_experience}>
              <input className="avcp-input" type="number" min="0" max="80" data-testid={TID.cpYears}
                     value={profile.years_experience || ""}
                     onChange={(e) => setField("years_experience", e.target.value)} />
            </Field>
            <Field label="Public phone" vis={visibilityMap.public_phone}>
              <input className="avcp-input" data-testid={TID.cpPublicPhone}
                     value={profile.public_phone || ""}
                     onChange={(e) => setField("public_phone", e.target.value)} />
            </Field>
            <Field label="Public email" vis={visibilityMap.public_email}>
              <input className="avcp-input" data-testid={TID.cpPublicEmail}
                     value={profile.public_email || ""}
                     onChange={(e) => setField("public_email", e.target.value)} />
            </Field>
          </div>
          <Field label="Short bio" hint="≤ 280 characters" vis={visibilityMap.short_bio}>
            <textarea className="avcp-input" rows="2" maxLength="280" data-testid={TID.cpShortBio}
                      value={profile.short_bio || ""}
                      onChange={(e) => setField("short_bio", e.target.value)} />
          </Field>
          <Field label="Long bio" hint="≤ 4000 characters" vis={visibilityMap.long_bio}>
            <textarea className="avcp-input" rows="5" maxLength="4000" data-testid={TID.cpLongBio}
                      value={profile.long_bio || ""}
                      onChange={(e) => setField("long_bio", e.target.value)} />
          </Field>
          <button data-testid={TID.cpSaveBasics} className="avcp-btn primary"
                  disabled={busy} onClick={saveBasics}>Save basics</button>
        </Section>

        {/* Media */}
        <Section id="media" title="Profile Photo & Company Logo" complete={isCmp("profile_photo") && isCmp("company_logo")}>
          <div className="grid md:grid-cols-2 gap-6">
            <div data-testid={TID.cpStepMedia}>
              <Field label="Profile photo" hint="JPEG / PNG / WebP · max 5 MB · EXIF GPS stripped" vis={visibilityMap.profile_photo}>
                <div className="flex items-center gap-4">
                  {photoUrl ? (
                    <img src={photoUrl} alt="profile" data-testid={TID.cpPhotoPreview}
                         className="w-20 h-20 rounded-full object-cover border border-[var(--avcp-line)]" />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-[var(--avcp-bg-2)] border border-[var(--avcp-line)] flex items-center justify-center text-xs text-[var(--avcp-ink-soft)]">No photo</div>
                  )}
                  <input type="file" accept="image/jpeg,image/png,image/webp" data-testid={TID.cpPhotoUpload}
                         onChange={(e) => uploadMedia(e.target.files?.[0], "/me/contractor-profile/photo", setPhotoUrl, "Profile photo")} />
                </div>
              </Field>
            </div>
            <div>
              <Field label="Company logo" hint="JPEG / PNG / WebP / SVG · max 5 MB" vis={visibilityMap.company_logo}>
                <div className="flex items-center gap-4">
                  {logoUrl ? (
                    <img src={logoUrl} alt="logo" data-testid={TID.cpLogoPreview}
                         className="w-20 h-20 rounded object-contain bg-white border border-[var(--avcp-line)] p-1" />
                  ) : (
                    <div className="w-20 h-20 rounded bg-[var(--avcp-bg-2)] border border-[var(--avcp-line)] flex items-center justify-center text-xs text-[var(--avcp-ink-soft)]">No logo</div>
                  )}
                  <input type="file" accept="image/jpeg,image/png,image/webp,image/svg+xml" data-testid={TID.cpLogoUpload}
                         onChange={(e) => uploadMedia(e.target.files?.[0], "/me/contractor-profile/company-logo", setLogoUrl, "Company logo")} />
                </div>
              </Field>
            </div>
          </div>
        </Section>

        {/* Crew capability */}
        <Section id="crew" title="Crew Capability & Acceptance" complete={isCmp("crew_capability")}>
          <div className="grid md:grid-cols-2 gap-4" data-testid={TID.cpStepCrew}>
            <Field label="Capability" vis={visibilityMap.crew_capability_kind}>
              <select className="avcp-input" data-testid={TID.cpCrewKind}
                      value={profile.crew_capability_kind || ""}
                      onChange={(e) => setField("crew_capability_kind", e.target.value)}>
                <option value="">Select…</option>
                <option value="solo">Solo</option>
                <option value="helper">Helper</option>
                <option value="lead">Lead</option>
                <option value="lead_plus_n">Lead + helpers</option>
                <option value="custom">Custom</option>
              </select>
            </Field>
            <Field label="Max helpers" vis={visibilityMap.max_helpers}>
              <input className="avcp-input" type="number" min="0" max="20" data-testid={TID.cpMaxHelpers}
                     value={profile.max_helpers ?? ""}
                     onChange={(e) => setField("max_helpers", e.target.value)} />
            </Field>
            <Field label="Accept lead work" vis={visibilityMap.accepts_lead_work}>
              <input type="checkbox" data-testid={TID.cpAcceptLead}
                     checked={!!profile.accepts_lead_work}
                     onChange={(e) => setField("accepts_lead_work", e.target.checked)} />
            </Field>
            <Field label="Accept helper work" vis={visibilityMap.accepts_helper_work}>
              <input type="checkbox" data-testid={TID.cpAcceptHelper}
                     checked={!!profile.accepts_helper_work}
                     onChange={(e) => setField("accepts_helper_work", e.target.checked)} />
            </Field>
            <Field label="Profile visibility (overall)" hint="Per-field visibility is admin-configured. This sets the marketplace-presence default.">
              <select className="avcp-input" data-testid={TID.cpVisibilitySelect}
                      value={profile.visibility || "platform_only"}
                      onChange={(e) => setField("visibility", e.target.value)}>
                <option value="private">Private (owner & super admin only)</option>
                <option value="platform_only">Platform only (logged-in platform users)</option>
                <option value="client_visible">Client visible (authenticated clients)</option>
                <option value="public">Public (future public pages)</option>
              </select>
            </Field>
          </div>
          <button data-testid={TID.cpSaveCrew} className="avcp-btn primary mt-4"
                  disabled={busy} onClick={saveCrew}>Save crew capability</button>
        </Section>

        {/* Workforce Offering (CP-14.2b) — replaces single Hourly Rate */}
        <Section id="rate" title="Workforce Offering" complete={isCmp("rate")}>
          <WorkforceOfferingEditor
            ownerKind="user"
            onSaved={async (items) => {
              // Mirror the first site-lead-capable row's rate (fallback: first row)
              // back into the Phase-12 contractor_profile.hourly_rate column so
              // existing booking flows keep working. Phase 12 ranking unchanged.
              const lead = items.find((it) => it.site_lead_capable) || items[0];
              if (!lead) return;
              try {
                await api("/me/contractor-profile", {
                  method: "PUT",
                  body: {
                    hourly_rate: Number(lead.hourly_rate),
                    currency: (lead.currency || "USD").toUpperCase(),
                  },
                });
                setField("hourly_rate", lead.hourly_rate);
                setField("currency", lead.currency);
              } catch (e) {
                // non-fatal: offering rows persisted; legacy mirror is best-effort
                console.warn("Phase-12 hourly_rate mirror failed:", e?.message);
              }
            }}
          />
        </Section>

        {/* Regions */}
        <Section id="regions" title="Service Regions" complete={isCmp("service_regions")}>
          <div className="grid md:grid-cols-3 gap-2" data-testid={TID.cpRegionsList}>
            {catalogs.regions.map((r) => {
              const on = !!regions.find((c) => c.code === r.code);
              return (
                <button key={r.code} onClick={() => toggleRegion(r.code)}
                        data-testid={`cp-region-${r.code}`}
                        className={`text-left px-3 py-2 rounded border text-sm transition ${on ? "border-[var(--avcp-primary)] bg-[var(--avcp-primary-soft)]" : "border-[var(--avcp-line)] hover:border-[var(--avcp-ink-soft)]"}`}>
                  <div className="font-medium">{r.label}</div>
                  <div className="text-xs text-[var(--avcp-ink-soft)] capitalize">{r.kind}</div>
                </button>
              );
            })}
          </div>
          <button data-testid={TID.cpSaveRegions} className="avcp-btn primary mt-4"
                  disabled={busy} onClick={saveRegions}>Save regions ({regions.length})</button>
        </Section>

        {/* Skills */}
        <Section id="skills" title="Skills" complete={isCmp("skills")}>
          <div className="space-y-2" data-testid={TID.cpSkillsList}>
            {catalogs.skills.map((s) => {
              const sel = skills.find((c) => c.code === s.code);
              return (
                <div key={s.code} className="flex items-center justify-between gap-3 px-3 py-2 rounded border border-[var(--avcp-line)]"
                     data-testid={`cp-skill-row-${s.code}`}>
                  <label className="flex items-center gap-3 flex-1 cursor-pointer">
                    <input type="checkbox" checked={!!sel} onChange={() => toggleSkill(s.code)}
                           data-testid={`cp-skill-toggle-${s.code}`} />
                    <span className="font-medium">{s.label}</span>
                    {s.category && <span className="text-xs text-[var(--avcp-ink-soft)]">{s.category}</span>}
                  </label>
                  {sel && (
                    <select className="avcp-input w-44" value={sel.proficiency}
                            data-testid={`cp-skill-prof-${s.code}`}
                            onChange={(e) => setSkillProf(s.code, e.target.value)}>
                      <option value="familiar">Familiar</option>
                      <option value="experienced">Experienced</option>
                      <option value="expert">Expert</option>
                    </select>
                  )}
                </div>
              );
            })}
          </div>
          <button data-testid={TID.cpSaveSkills} className="avcp-btn primary mt-4"
                  disabled={busy} onClick={saveSkills}>Save skills ({skills.length})</button>
        </Section>

        {/* Certifications */}
        <Section id="certs" title="Certifications" complete={isCmp("certifications")}>
          <div className="flex items-center gap-2 mb-3">
            <select className="avcp-input flex-1" defaultValue="" data-testid={TID.cpAddCert}
                    onChange={(e) => { if (e.target.value) { addCert(e.target.value); e.target.value = ""; } }}>
              <option value="">Add certification…</option>
              {catalogs.certifications
                .filter((c) => !certs.find((cc) => cc.code === c.code))
                .map((c) => (
                  <option key={c.code} value={c.code}>{c.label} {c.issuer ? `· ${c.issuer}` : ""}</option>
                ))}
            </select>
          </div>
          <ul className="space-y-2" data-testid={TID.cpCertsList}>
            {certs.length === 0 && <li className="text-sm text-[var(--avcp-ink-soft)]">No certifications added yet.</li>}
            {certs.map((c) => (
              <li key={c.id} className="flex items-center justify-between px-3 py-2 rounded border border-[var(--avcp-line)]"
                  data-testid={`cp-cert-row-${c.code}`}>
                <div>
                  <div className="font-medium">{c.label}</div>
                  {c.issuer && <div className="text-xs text-[var(--avcp-ink-soft)]">{c.issuer}</div>}
                </div>
                <button className="avcp-btn ghost" onClick={() => removeCert(c.id)}
                        data-testid={TID.cpRemoveCert}>Remove</button>
              </li>
            ))}
          </ul>
        </Section>

        {/* Languages */}
        <Section id="langs" title="Languages" complete={isCmp("languages")}>
          <div className="flex items-center gap-2 mb-3">
            <select className="avcp-input flex-1" defaultValue="" data-testid={TID.cpLangSelect}
                    onChange={(e) => { if (e.target.value) { addLang(e.target.value); e.target.value = ""; } }}>
              <option value="">Add language…</option>
              {catalogs.languages
                .filter((c) => !languages.find((l) => l.code === c.code))
                .map((c) => (
                  <option key={c.code} value={c.code}>{c.label}</option>
                ))}
            </select>
            <button data-testid={TID.cpAddLang} className="avcp-btn ghost" onClick={saveLangs}>Save list</button>
          </div>
          <ul className="space-y-2">
            {languages.length === 0 && <li className="text-sm text-[var(--avcp-ink-soft)]">No languages added yet.</li>}
            {languages.map((l) => (
              <li key={l.code} className="flex items-center justify-between px-3 py-2 rounded border border-[var(--avcp-line)]"
                  data-testid={`cp-lang-row-${l.code}`}>
                <div className="font-medium">{l.code.toUpperCase()}</div>
                <div className="flex items-center gap-2">
                  <select className="avcp-input w-44" value={l.proficiency}
                          onChange={(e) => setLangProf(l.code, e.target.value)}>
                    <option value="conversational">Conversational</option>
                    <option value="fluent">Fluent</option>
                    <option value="native">Native</option>
                  </select>
                  <button className="avcp-btn ghost" onClick={() => removeLang(l.code)}>Remove</button>
                </div>
              </li>
            ))}
          </ul>
          <button data-testid={TID.cpSaveLangs} className="avcp-btn primary mt-4"
                  disabled={busy} onClick={saveLangs}>Save languages</button>
        </Section>

        {/* Travel */}
        <Section id="travel" title="Travel Preferences" complete={isCmp("travel_preferences")}>
          <div className="grid md:grid-cols-2 gap-4" data-testid={TID.cpStepTravel}>
            <Field label="Travel radius" vis={visibilityMap.travel_radius_kind}>
              <select className="avcp-input" data-testid={TID.cpTravelRadius}
                      value={profile.travel_radius_kind || ""}
                      onChange={(e) => setField("travel_radius_kind", e.target.value)}>
                <option value="">Select…</option>
                <option value="local">Local</option>
                <option value="province_state">Province / State</option>
                <option value="national">National</option>
                <option value="international">International</option>
              </select>
            </Field>
            <Field label="Willing to travel" vis={visibilityMap.willing_to_travel}>
              <input type="checkbox" data-testid={TID.cpWillingTravel}
                     checked={!!profile.willing_to_travel}
                     onChange={(e) => setField("willing_to_travel", e.target.checked)} />
            </Field>
            <Field label="Willing to fly" vis={visibilityMap.willing_to_fly}>
              <input type="checkbox" data-testid={TID.cpWillingFly}
                     checked={!!profile.willing_to_fly}
                     onChange={(e) => setField("willing_to_fly", e.target.checked)} />
            </Field>
            <Field label="Willing to stay overnight" vis={visibilityMap.willing_to_stay_overnight}>
              <input type="checkbox" data-testid={TID.cpWillingOvernight}
                     checked={!!profile.willing_to_stay_overnight}
                     onChange={(e) => setField("willing_to_stay_overnight", e.target.checked)} />
            </Field>
          </div>
          <button data-testid={TID.cpSaveTravel} className="avcp-btn primary mt-4"
                  disabled={busy} onClick={saveTravel}>Save travel preferences</button>
        </Section>

        {/* Insurance */}
        <Section id="insurance" title="Insurance (Private)" complete={isCmp("insurance")}>
          <p className="text-xs text-[var(--avcp-ink-soft)] mb-3">
            Insurance details are <strong>private</strong> — visible only to you and Super Admin. Required by some clients before booking.
          </p>
          <div className="grid md:grid-cols-2 gap-4" data-testid={TID.cpStepInsurance}>
            <Field label="Provider" vis="private">
              <input className="avcp-input" data-testid={TID.cpInsuranceProvider}
                     value={profile.insurance_provider || profile.insurance_json?.provider || ""}
                     onChange={(e) => setField("insurance_provider", e.target.value)} />
            </Field>
            <Field label="Policy number" vis="private">
              <input className="avcp-input" data-testid={TID.cpInsurancePolicy}
                     value={profile.insurance_policy || profile.insurance_json?.policy_number || ""}
                     onChange={(e) => setField("insurance_policy", e.target.value)} />
            </Field>
            <Field label="Coverage amount" vis="private">
              <input className="avcp-input" type="number" min="0" data-testid={TID.cpInsuranceCoverage}
                     value={profile.insurance_coverage || profile.insurance_json?.coverage_amount || ""}
                     onChange={(e) => setField("insurance_coverage", e.target.value)} />
            </Field>
            <Field label="Currency" vis="private">
              <input className="avcp-input" maxLength="3" data-testid={TID.cpInsuranceCurrency}
                     value={profile.insurance_currency || profile.insurance_json?.currency || ""}
                     onChange={(e) => setField("insurance_currency", e.target.value.toUpperCase())} />
            </Field>
          </div>
          <button data-testid={TID.cpSaveInsurance} className="avcp-btn primary mt-4"
                  disabled={busy} onClick={saveInsurance}>Save insurance</button>
        </Section>

        {/* Visibility map (read-only for non-admin) */}
        <Section id="visibility" title="Per-Field Visibility (Platform Configuration)">
          <p className="text-xs text-[var(--avcp-ink-soft)] mb-3">
            Each profile field has a configurable visibility level. Super Admin can change these
            without schema changes; this is the live configuration.
          </p>
          <div className="grid md:grid-cols-2 gap-2">
            {Object.entries(visibilityMap).sort().map(([k, v]) => (
              <div key={k} className="flex items-center justify-between px-3 py-2 rounded bg-[var(--avcp-bg-2)] text-sm">
                <span className="font-mono text-xs">{k}</span>
                <VisibilityBadge mode={v} field={k} />
              </div>
            ))}
          </div>
        </Section>
      </div>
    </Layout>
  );
}

export default ContractorProfile;
